import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import {
  getProductAttributes,
  createProductAttribute,
  updateProductAttribute,
  deleteProductAttribute,
  createAttributeOption,
  updateAttributeOption,
  deleteAttributeOption,
} from "../../api/productAttributeService";
import { getCategoryAttributes, setCategoryAttributes } from "../../api/categoryService";
import { getCategories } from "../../api/adminService";

const INPUT_TYPES = [
  { value: 1, label: "Dropdown" },
  { value: 2, label: "Text" },
  { value: 3, label: "Number" },
];

function inputTypeLabel(value) {
  return INPUT_TYPES.find((t) => t.value === value)?.label || "Dropdown";
}

export default function Attributes() {
  const [tab, setTab] = useState(0);
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function reload() {
    setLoading(true);
    try {
      const data = await getProductAttributes();
      setAttributes(data);
    } catch {
      setError("Could not load attributes. Is the API running?");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ mb: 3, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" } }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Avatar sx={{ bgcolor: "primary.main", width: 44, height: 44 }}>
            <TuneRoundedIcon />
          </Avatar>
          <Box>
            <Typography variant="h4">Attributes</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage product attributes (Size, Color, Volume…) and assign them to categories.
            </Typography>
          </Box>
        </Stack>
        <Tooltip title="Refresh">
          <IconButton
            onClick={reload}
            sx={{ border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
          >
            <RefreshRoundedIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {typeof error === "string" ? error : "Something went wrong."}
        </Alert>
      )}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2.5 }}>
        <Tab label="Attribute Definitions" />
        <Tab label="Assign to Categories" />
      </Tabs>

      {tab === 0 ? (
        <DefinitionsPanel attributes={attributes} loading={loading} reload={reload} setError={setError} />
      ) : (
        <AssignmentPanel attributes={attributes} attributesLoading={loading} setError={setError} />
      )}
    </Box>
  );
}

function DefinitionsPanel({ attributes, loading, reload, setError }) {
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(null); // { attribute } | { attribute: null }
  const [deleteTarget, setDeleteTarget] = useState(null); // attribute
  const [optionDialog, setOptionDialog] = useState(null); // { attribute, option? }
  const [optionDeleteTarget, setOptionDeleteTarget] = useState(null); // { attribute, option }
  const [expanded, setExpanded] = useState({});

  const term = search.trim().toLowerCase();
  const filtered = term ? attributes.filter((a) => a.name.toLowerCase().includes(term)) : attributes;

  async function handleSave(values) {
    try {
      if (dialog.attribute) {
        await updateProductAttribute(dialog.attribute.attributeId, values);
      } else {
        await createProductAttribute(values);
      }
      setDialog(null);
      await reload();
    } catch (err) {
      setError(err.response?.data || "Could not save this attribute.");
      throw err;
    }
  }

  async function confirmDelete() {
    try {
      await deleteProductAttribute(deleteTarget.attributeId);
      setDeleteTarget(null);
      await reload();
    } catch (err) {
      setError(err.response?.data || "This attribute could not be deleted.");
      setDeleteTarget(null);
    }
  }

  async function handleSaveOption(values) {
    const { attribute, option } = optionDialog;
    try {
      if (option) {
        await updateAttributeOption(attribute.attributeId, option.optionId, values);
      } else {
        await createAttributeOption(attribute.attributeId, values);
      }
      setOptionDialog(null);
      await reload();
    } catch (err) {
      setError(err.response?.data || "Could not save this option.");
      throw err;
    }
  }

  async function confirmDeleteOption() {
    const { attribute, option } = optionDeleteTarget;
    try {
      await deleteAttributeOption(attribute.attributeId, option.optionId);
      setOptionDeleteTarget(null);
      await reload();
    } catch (err) {
      setError(err.response?.data || "This option could not be deleted.");
      setOptionDeleteTarget(null);
    }
  }

  return (
    <>
      <Paper variant="outlined" sx={{ mb: 2.5, p: 2, borderRadius: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between" }}
        >
          <TextField
            placeholder="Search attributes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ minWidth: 280 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch("")}>
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={() => setDialog({ attribute: null })}
          >
            New attribute
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        {loading ? (
          <Stack spacing={1} sx={{ p: 1.5 }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rounded" height={64} />
            ))}
          </Stack>
        ) : filtered.length === 0 ? (
          <Stack spacing={1} sx={{ alignItems: "center", justifyContent: "center", py: 6, px: 2 }}>
            <TuneRoundedIcon sx={{ fontSize: 32, color: "text.disabled" }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {attributes.length === 0 ? "No attributes yet" : "No matches for this search"}
            </Typography>
            {attributes.length === 0 && (
              <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => setDialog({ attribute: null })}>
                Add attribute
              </Button>
            )}
          </Stack>
        ) : (
          <List disablePadding>
            {filtered.map((attribute) => {
              const isOpen = Boolean(expanded[attribute.attributeId]);
              const isDropdown = attribute.inputType === 1;
              return (
                <React.Fragment key={attribute.attributeId}>
                  <ListItem
                    disablePadding
                    divider={!isOpen}
                    sx={{ "&:hover .row-actions, &:focus-within .row-actions": { opacity: 1 } }}
                    secondaryAction={
                      <Stack direction="row" spacing={0.25} className="row-actions" sx={{ opacity: 0.35, transition: "opacity .15s" }}>
                        {isDropdown && (
                          <Tooltip title={isOpen ? "Hide options" : "Show options"}>
                            <IconButton
                              size="small"
                              onClick={() => setExpanded((prev) => ({ ...prev, [attribute.attributeId]: !isOpen }))}
                            >
                              {isOpen ? <ExpandLessRoundedIcon fontSize="small" /> : <ExpandMoreRoundedIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => setDialog({ attribute })}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => setDeleteTarget(attribute)}>
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    }
                  >
                    <ListItemButton
                      onClick={() => isDropdown && setExpanded((prev) => ({ ...prev, [attribute.attributeId]: !isOpen }))}
                      sx={{ pr: 14, py: 1.25, cursor: isDropdown ? "pointer" : "default" }}
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography sx={{ fontWeight: 600 }}>{attribute.name}</Typography>
                            <Chip size="small" label={inputTypeLabel(attribute.inputType)} variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                            {attribute.unit && (
                              <Chip size="small" label={attribute.unit} sx={{ height: 20, fontSize: 11 }} />
                            )}
                            {isDropdown && (
                              <Chip size="small" label={`${attribute.options.length} option${attribute.options.length === 1 ? "" : "s"}`} variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                            )}
                          </Stack>
                        }
                        secondary={
                          [attribute.nameArabic, attribute.nameHebrew].filter(Boolean).join(" · ") || undefined
                        }
                      />
                    </ListItemButton>
                  </ListItem>

                  {isDropdown && (
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <Box sx={{ pl: 3, pr: 2, py: 1.5, bgcolor: "action.hover" }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ alignItems: "center" }}>
                          {attribute.options
                            .slice()
                            .sort((a, b) => a.displayOrder - b.displayOrder)
                            .map((option) => (
                              <Chip
                                key={option.optionId}
                                label={option.value}
                                onDelete={() => setOptionDeleteTarget({ attribute, option })}
                                onClick={() => setOptionDialog({ attribute, option })}
                                variant="outlined"
                                sx={{ bgcolor: "background.paper" }}
                              />
                            ))}
                          <Chip
                            icon={<AddRoundedIcon fontSize="small" />}
                            label="Add option"
                            onClick={() => setOptionDialog({ attribute, option: null })}
                            color="primary"
                            variant="outlined"
                          />
                        </Stack>
                      </Box>
                      <Divider />
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>

      {dialog && <AttributeDialog dialog={dialog} onClose={() => setDialog(null)} onSave={handleSave} />}

      {optionDialog && (
        <OptionDialog dialog={optionDialog} onClose={() => setOptionDialog(null)} onSave={handleSaveOption} />
      )}

      {deleteTarget && (
        <Dialog open onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
          <DialogTitle>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "error.main", width: 36, height: 36 }}>
                <WarningAmberRoundedIcon fontSize="small" />
              </Avatar>
              <span>Delete attribute?</span>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              You're about to delete <strong>{deleteTarget.name}</strong>. This cannot be undone. If it's still
              assigned to a category, deletion will be blocked.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {optionDeleteTarget && (
        <Dialog open onClose={() => setOptionDeleteTarget(null)} fullWidth maxWidth="xs">
          <DialogTitle>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "error.main", width: 36, height: 36 }}>
                <WarningAmberRoundedIcon fontSize="small" />
              </Avatar>
              <span>Delete option?</span>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              You're about to delete <strong>{optionDeleteTarget.option.value}</strong>. If it's already used by a
              product or variant, deletion will be blocked.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOptionDeleteTarget(null)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={confirmDeleteOption}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

function AttributeDialog({ dialog, onClose, onSave }) {
  const { attribute } = dialog;
  const [name, setName] = useState(attribute?.name || "");
  const [nameArabic, setNameArabic] = useState(attribute?.nameArabic || "");
  const [nameHebrew, setNameHebrew] = useState(attribute?.nameHebrew || "");
  const [inputType, setInputType] = useState(attribute?.inputType ?? 1);
  const [unit, setUnit] = useState(attribute?.unit || "");
  const [saving, setSaving] = useState(false);

  async function submit() {
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        nameArabic: nameArabic.trim() || null,
        nameHebrew: nameHebrew.trim() || null,
        inputType,
        unit: unit.trim() || null,
      });
    } catch {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {attribute ? "Edit attribute" : "New attribute"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth autoFocus />
          <TextField label="Name (Arabic)" value={nameArabic} onChange={(e) => setNameArabic(e.target.value)} fullWidth />
          <TextField label="Name (Hebrew)" value={nameHebrew} onChange={(e) => setNameHebrew(e.target.value)} fullWidth />
          <Select value={inputType} onChange={(e) => setInputType(Number(e.target.value))} fullWidth size="small">
            {INPUT_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
          <TextField
            label="Unit (optional)"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            helperText="e.g. ml, kg, cm — shown next to the value"
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!name.trim() || saving} onClick={submit}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function OptionDialog({ dialog, onClose, onSave }) {
  const { attribute, option } = dialog;
  const [value, setValue] = useState(option?.value || "");
  const [valueArabic, setValueArabic] = useState(option?.valueArabic || "");
  const [valueHebrew, setValueHebrew] = useState(option?.valueHebrew || "");
  const [displayOrder, setDisplayOrder] = useState(option?.displayOrder ?? attribute.options.length);
  const [saving, setSaving] = useState(false);

  async function submit() {
    setSaving(true);
    try {
      await onSave({
        value: value.trim(),
        valueArabic: valueArabic.trim() || null,
        valueHebrew: valueHebrew.trim() || null,
        displayOrder: Number(displayOrder) || 0,
      });
    } catch {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {option ? "Edit option" : "New option"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          for {attribute.name}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Value" value={value} onChange={(e) => setValue(e.target.value)} fullWidth autoFocus />
          <TextField label="Value (Arabic)" value={valueArabic} onChange={(e) => setValueArabic(e.target.value)} fullWidth />
          <TextField label="Value (Hebrew)" value={valueHebrew} onChange={(e) => setValueHebrew(e.target.value)} fullWidth />
          <TextField
            label="Display Order"
            type="number"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
            helperText="Lower numbers appear first"
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!value.trim() || saving} onClick={submit}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AssignmentPanel({ attributes, attributesLoading, setError }) {
  const [levels, setLevels] = useState({ 1: [], 2: [], 3: [] });
  const [levelsLoading, setLevelsLoading] = useState({ 1: true, 2: false, 3: false });
  const [selected, setSelected] = useState({ 1: "", 2: "", 3: "" });
  const [rows, setRows] = useState(null); // { [attributeId]: { included, isRequired, isVariant, displayOrder } }
  const [rowsLoading, setRowsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getCategories({ level: 1 })
      .then((data) => setLevels((prev) => ({ ...prev, 1: data })))
      .catch(() => setError("Could not load categories."))
      .finally(() => setLevelsLoading((prev) => ({ ...prev, 1: false })));
  }, [setError]);

  function handleSelectMain(id) {
    setSelected({ 1: id, 2: "", 3: "" });
    setRows(null);
    setLevels((prev) => ({ ...prev, 2: [], 3: [] }));
    if (!id) return;
    setLevelsLoading((prev) => ({ ...prev, 2: true }));
    getCategories({ level: 2, parentCategoryId: id })
      .then((data) => setLevels((prev) => ({ ...prev, 2: data })))
      .catch(() => setError("Could not load subcategories."))
      .finally(() => setLevelsLoading((prev) => ({ ...prev, 2: false })));
  }

  function handleSelectSub(id) {
    setSelected((prev) => ({ ...prev, 2: id, 3: "" }));
    setRows(null);
    setLevels((prev) => ({ ...prev, 3: [] }));
    if (!id) return;
    setLevelsLoading((prev) => ({ ...prev, 3: true }));
    getCategories({ level: 3, parentCategoryId: id })
      .then((data) => setLevels((prev) => ({ ...prev, 3: data })))
      .catch(() => setError("Could not load items."))
      .finally(() => setLevelsLoading((prev) => ({ ...prev, 3: false })));
  }

  function handleSelectLeaf(id) {
    setSelected((prev) => ({ ...prev, 3: id }));
    setSaved(false);
    if (!id) {
      setRows(null);
      return;
    }
    setRowsLoading(true);
    getCategoryAttributes(id)
      .then((existing) => {
        const map = {};
        attributes.forEach((a, i) => {
          const match = existing.find((e) => e.attributeId === a.attributeId);
          map[a.attributeId] = {
            included: Boolean(match),
            isRequired: match?.isRequired ?? false,
            isVariant: match?.isVariant ?? false,
            displayOrder: match?.displayOrder ?? i,
          };
        });
        setRows(map);
      })
      .catch(() => setError("Could not load this category's attributes."))
      .finally(() => setRowsLoading(false));
  }

  function updateRow(attributeId, patch) {
    setSaved(false);
    setRows((prev) => ({ ...prev, [attributeId]: { ...prev[attributeId], ...patch } }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {
        attributes: Object.entries(rows)
          .filter(([, v]) => v.included)
          .map(([attributeId, v]) => ({
            attributeId: Number(attributeId),
            isRequired: v.isRequired,
            isVariant: v.isVariant,
            displayOrder: Number(v.displayOrder) || 0,
          })),
      };
      await setCategoryAttributes(selected[3], payload);
      setSaved(true);
    } catch (err) {
      setError(err.response?.data || "Could not save this category's attributes.");
    } finally {
      setSaving(false);
    }
  }

  const leafSelected = Boolean(selected[3]);

  return (
    <Stack spacing={2.5}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
          Pick an item-level category
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <Select
            size="small"
            displayEmpty
            value={selected[1]}
            onChange={(e) => handleSelectMain(e.target.value)}
            sx={{ minWidth: 220 }}
            disabled={levelsLoading[1]}
          >
            <MenuItem value="">
              <em>Main category…</em>
            </MenuItem>
            {levels[1].map((c) => (
              <MenuItem key={c.categoryId} value={c.categoryId}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            displayEmpty
            value={selected[2]}
            onChange={(e) => handleSelectSub(e.target.value)}
            sx={{ minWidth: 220 }}
            disabled={!selected[1] || levelsLoading[2]}
          >
            <MenuItem value="">
              <em>Subcategory…</em>
            </MenuItem>
            {levels[2].map((c) => (
              <MenuItem key={c.categoryId} value={c.categoryId}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            displayEmpty
            value={selected[3]}
            onChange={(e) => handleSelectLeaf(e.target.value)}
            sx={{ minWidth: 220 }}
            disabled={!selected[2] || levelsLoading[3]}
          >
            <MenuItem value="">
              <em>Item…</em>
            </MenuItem>
            {levels[3].map((c) => (
              <MenuItem key={c.categoryId} value={c.categoryId}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </Paper>

      {!leafSelected ? (
        <Paper variant="outlined" sx={{ borderRadius: 2, py: 6 }}>
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <TuneRoundedIcon sx={{ fontSize: 32, color: "text.disabled" }} />
            <Typography variant="body2" color="text.secondary">
              Select a main category, subcategory, and item to manage its attributes.
            </Typography>
          </Stack>
        </Paper>
      ) : rowsLoading || attributesLoading || !rows ? (
        <Stack spacing={1}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={56} />
          ))}
        </Stack>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Toggle which attributes apply to <strong>{levels[3].find((c) => c.categoryId === selected[3])?.name}</strong>,
              whether they're required, and whether they generate variants (dropdown attributes only).
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<SaveRoundedIcon />}
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? "Saving…" : saved ? "Saved" : "Save"}
            </Button>
          </Stack>
          <Divider />
          <List disablePadding>
            {attributes.map((attribute) => {
              const row = rows[attribute.attributeId];
              const isDropdown = attribute.inputType === 1;
              return (
                <ListItem key={attribute.attributeId} divider sx={{ py: 1.25 }}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 2 }} alignItems={{ sm: "center" }} sx={{ width: "100%" }}>
                    <FormControlLabel
                      sx={{ minWidth: 200, m: 0 }}
                      control={
                        <Switch
                          checked={row.included}
                          onChange={(e) => updateRow(attribute.attributeId, { included: e.target.checked })}
                        />
                      }
                      label={
                        <Stack>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {attribute.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {inputTypeLabel(attribute.inputType)}
                            {attribute.unit ? ` · ${attribute.unit}` : ""}
                          </Typography>
                        </Stack>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={row.isRequired}
                          disabled={!row.included}
                          onChange={(e) => updateRow(attribute.attributeId, { isRequired: e.target.checked })}
                        />
                      }
                      label="Required"
                    />
                    <Tooltip title={isDropdown ? "" : "Only dropdown attributes can generate variants"}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={row.isVariant}
                            disabled={!row.included || !isDropdown}
                            onChange={(e) => updateRow(attribute.attributeId, { isVariant: e.target.checked })}
                          />
                        }
                        label="Variant"
                      />
                    </Tooltip>
                    <TextField
                      label="Order"
                      type="number"
                      size="small"
                      value={row.displayOrder}
                      disabled={!row.included}
                      onChange={(e) => updateRow(attribute.attributeId, { displayOrder: e.target.value })}
                      sx={{ width: 90 }}
                    />
                  </Stack>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      )}
    </Stack>
  );
}
